import { cn } from '@creator/ui/utils/ui';
import { canViewComment, canViewPendingComment } from '@src/model/upvote/permission';
import { type CommentCollectionCompositeKeyParams, type CommentCollectionType, getComment } from '@src/store/comments/comments';
import { useComment } from '@src/store/comments/hooks/useComment';
import { useCommentLoader } from '@src/store/comments/hooks/useCommentLoader';
import { usePaginatedComments } from '@src/store/comments/hooks/usePaginatedComments';
import { type CommentSortOrder, CommentStatus } from '@src/store/contentEntities/model';
import type { FC } from 'react';
import { memo, useEffect, useLayoutEffect, useMemo } from 'react';
import Comment from '../Comment/Comment';
import { LoadMoreCommentsButton } from '../Comment/LoadMoreCommentsButton';
import EmptyState from '../EmptyState';

interface CommentListProps {
    tokenName: string;
    className?: string;
    type: CommentCollectionType;
    postId?: string;
    userId?: string;
    parentCommentId?: string;
    includeDeleted?: boolean;
    includeReplies?: boolean;
    commentClassName?: string;
    highlightCommentId?: string;
    fetchLimit?: number;
    goToCommentOnClick?: boolean;
    sortOrder?: CommentSortOrder;
}

const CommentList: FC<CommentListProps> = props => {
    const { type, postId, userId, highlightCommentId = '', parentCommentId, includeDeleted, tokenName, className = '', fetchLimit, sortOrder } = props;

    const hookParams: CommentCollectionCompositeKeyParams = {
        type,
        postId,
        userId,
        parentCommentId,
        includeDeleted,
        tokenName,
        fetchLimit,
        sortOrder,
    };

    const { commentIds, loadMore, loadStatus, hasMore } = usePaginatedComments({ ...hookParams, includeMyReactions: true });

    const { load: loadHighlightComment } = useCommentLoader({ commentId: highlightCommentId });
    const highlightComment = useComment(highlightCommentId);

    function getBaseClassName() {
        const base = '';
        if (parentCommentId) return base;
        return cn(base, 'divide-y divide-gray-200 dark:divide-gray-800');
    }

    useLayoutEffect(() => {
        if (!highlightCommentId || highlightComment) return;
        loadHighlightComment();
    }, [highlightComment, highlightCommentId]);

    useEffect(() => {
        if (commentIds.length > 0) return; // skip load if there are items already
        loadMore();
    }, [loadMore, commentIds.length]);

    function renderLoadMoreButton() {
        if (!hasMore) return;
        return (
            <LoadMoreCommentsButton
                className={parentCommentId ? 'px-4' : 'px-4 py-3'}
                userId={userId}
                tokenName={tokenName}
                postId={postId}
                includeDeleted={includeDeleted}
                parentCommentId={parentCommentId}
                type={type}
                sortOrder={sortOrder}
            />
        );
    }

    function renderComment(commentId: string, isHighlighted: boolean) {
        const { commentClassName = '', highlightCommentId, includeReplies, goToCommentOnClick } = props;
        // Skip rendering the highlighted comment, it will be rendered above the list
        if (!isHighlighted && commentId === highlightCommentId) return null;
        const comment = getComment(commentId);
        if (!comment) return null;
        // Don't render unpublished comments if the user doesn't have permission to view them or they have no replies
        // Exception: Always show highlighted comments regardless of permissions
        if (!isHighlighted && comment.status !== CommentStatus.PUBLISHED && !canViewComment(commentId, tokenName) && comment.numComments === 0) return null;
        return (
            <Comment
                goToCommentOnClick={goToCommentOnClick}
                postId={postId}
                parentCommentId={parentCommentId}
                size={parentCommentId ? 'md' : 'lg'}
                className={cn('', commentClassName)}
                key={commentId}
                includeReplies={includeReplies}
                commentId={commentId}
                isHighlighted={isHighlighted}
                tokenName={tokenName}
            />
        );
    }

    function renderHighlightedComment() {
        if (!highlightCommentId) return null;
        return renderComment(highlightCommentId, true);
    }
    function renderEmptyState() {
        if (loadStatus !== 'success' || commentIds.length > 0) return null;
        return <EmptyState type={type} context="comment" />;
    }

    return (
        <div className={cn(getBaseClassName(), className)}>
            {renderEmptyState()}
            {renderHighlightedComment()}
            {commentIds.map(id => renderComment(id, false))}
            {renderLoadMoreButton()}
        </div>
    );
};

export default memo(CommentList);
